import { useState, useCallback, useEffect, SetStateAction } from 'react'
import {
    DEFAULT_ENCODER,
    DEFAULT_DECODER,
    getValue,
    setItemByValue,
    StorageType,
} from '../utils/storageUtils'

type HookParams<T> = {
    key: string
    defaultValue: T
    getItemValidation?: (v: T) => boolean
    /**
     * Should be synchronous for the moment
     */
    encode?: (v: T) => string
    /**
     * Should be synchronous for the moment
     */
    decode?: (str: string) => unknown
    persistence?: StorageType
}

/**
 * - It's recommended to use this hook inside a context (only one state created for a given key)
 * - defaultValue, getItemValidation, encode, decode should be stable values.
 * - The generic type T shouldn't be a function
 */
export const useBrowerStorage = <T>({
    key,
    defaultValue,
    getItemValidation,
    encode = DEFAULT_ENCODER,
    decode = DEFAULT_DECODER,
    persistence,
}: HookParams<T>): {
    value: T
    setValue: (v: SetStateAction<T>) => void
} => {
    const [state, setState] = useState<T>(() => {
        const result = getValue({ key, getItemValidation, decode, persistence })
        return result.isSuccessful ? result.value : defaultValue
    })

    const setValue = useCallback(
        (v: SetStateAction<T>) => {
            if (typeof v === 'function') {
                const result = getValue({ key, getItemValidation, decode, persistence })
                const currentValue = result.isSuccessful ? result.value : defaultValue // is equal to state in theory
                // @ts-ignore (T shouldn't be a function)
                const newValue = v(currentValue)

                const { isSuccessful } = setItemByValue({
                    key,
                    value: newValue,
                    encode,
                    persistence,
                })

                // setState if browserStorage.setItem was successful
                if (isSuccessful) {
                    setState(v)
                }
                return
            }

            const { isSuccessful } = setItemByValue({ key, value: v, encode, persistence })

            // setState if browserStorage.setItem was successful
            if (isSuccessful) {
                setState(v)
            }
        },
        [decode, defaultValue, encode, getItemValidation, key, persistence],
    )

    // update state when browserStorage changes (by other tabs)
    useEffect(() => {
        const onChange = (e: StorageEvent) => {
            if (e.key !== key) {
                return
            }
            const result = getValue({ key, getItemValidation, decode, persistence })

            setState(result.isSuccessful ? result.value : defaultValue)
        }
        window.addEventListener('storage', onChange)
        return () => window.removeEventListener('storage', onChange)
    }, [decode, defaultValue, getItemValidation, key, persistence])

    return { value: state, setValue }
}
